import { Suspense } from 'react';
import { connection } from 'next/server';

/**
 * Connection marker component for Next.js 16 PPR
 * Signals to Next.js where dynamic content begins
 */
const Connection = async () => {
  await connection();

  return null;
};

/**
 * Dynamic marker wrapped in Suspense for Next.js 16 Partial Prerendering (PPR).
 *
 * This component should be placed at the END of your component tree to maximize
 * the static shell that can be pre-rendered. It signals to Next.js where dynamic
 * content begins, allowing the framework to pre-render everything before this marker
 * and defer dynamic parts.
 *
 * **Usage:**
 * ```tsx
 * export default async function Layout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <Header />
 *         {children}
 *         <Footer />
 *         <DynamicMarker />  // Place at the end
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * **Why at the end?**
 * - Maximizes static shell size (everything before marker is static)
 * - Minimizes Time to First Byte (TTFB)
 * - Improves Core Web Vitals (LCP, FCP)
 *
 * **References:**
 * - Next.js PPR: https://nextjs.org/docs/app/building-your-application/rendering/partial-prerendering
 * - connection() API: https://nextjs.org/docs/app/api-reference/functions/connection
 *
 * @returns JSX element with Suspense boundary and connection marker
 */
export async function DynamicMarker() {
  return (
    <Suspense>
      <Connection />
    </Suspense>
  );
}
